import { loadingState, beginRefresh } from '../core/loadingState.js';
/**
 * leads-route.js — client-saas /leads
 *
 * The payoff of the magnet loop: the leads your magnets captured. Tenant-scoped server-side
 * (m5mLead → listRecordsByOwner(ownerGuids)), so each org sees only its own; resellers see their
 * subtree; managed clients see their own (read-only is fine — viewing only). Lists leads with a
 * status filter, inline status change (PUT /m5t/v5/m5mLead/:id), a detail drawer, and CSV export.
 * Each lead is linked to the magnet that captured it (magnet_guid → /magnets name map).
 */

import AuthController from '../services/AuthController.js';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { inPortfolio, ensurePortfolioStyles, clientNameMap, clientChipHTML, initPicker } from '../services/portfolio-view.js';
import { HANDOFF, takeHandoff, takeLeadsHandoff, leadsHandoffScope, setMagnetResultsHandoff } from '../core/routeHandoff.js';
import { LIST_CAP, pageSizeControlHTML, wirePageSize } from '../core/listExpand.js';
import { fetchIntegrations, connFor, statusOf, PROVIDERS as INTG_PROVIDERS } from '../services/integrations-shared.js';
import { planAllowsClient } from '../services/planClient.js';
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';
import { lsaActivityHTML } from './leads-lsa-activity.js';
import { mountLeadInquiryHistory } from './lead-inquiry-history.js';
import { readLeadView, saveLeadView, readLeadListView, saveLeadListView, takeLeadListVisit, rememberLeadConnectionReturn, clearLeadBootRoute } from '../core/leadWorkspaceMemory.js';
import '../styles/leads-workspace.css';

let stylesInjected = false;
let inquiryHistory = null, panelLead = null;
let loadEpoch = 0, searchTimer, currentScope = '';
const viewMemory = new Map();
const listViewMemory = new Map();
// `book` = reseller portfolio mode: the ONE blended cross-client view in the app
// (an agency triage inbox). Rows carry a client badge so attribution is explicit.
const state = {
  leads: [], magnetNames: {}, filter: 'ALL', canManage: false, book: false, ownerNames: {},
  // `day` is a single YYYY-MM-DD or '' — set by a trend-chart drill-through, cleared
  // from its own chip. Unlike the other filters it has no permanent control, because
  // picking an arbitrary day by hand is what the chart is for.
  clientFilter: 'ALL', search: '', shown: LIST_CAP, selectedId: null, day: '',
  // Newest-first is what the list already showed (the API hands leads back in that
  // order), so this default changes nothing on first paint — it just makes the order
  // explicit and reversible instead of implicit.
  sort: { key: 'captured', dir: 'desc' },
  // Two standing filters this page never had. `magnetFilter` is a magnet guid or 'ALL';
  // `range` is one of RANGES below. Both default to everything, so a fresh visit looks
  // exactly as it did before.
  magnetFilter: 'ALL', sourceFilter: 'ALL', range: 'ALL',
  // The page size the reader chose ('ALL' or a number). state.shown is derived from it;
  // the five filter-reset sites below go back to the FIRST page of that size, not back
  // to 25 — resetting the size on every filter change would undo the choice constantly.
  pageSize: LIST_CAP,
  // How many leads exist server-side vs how many we were handed. Searching and filtering
  // happen in the browser, so a truncated fetch has to be said out loud.
  total: 0, truncated: false, workspace: null, offset: 0, view: 'compact', filtersOpen: false,
  from: '', to: '', leadId: '', leadGuid: '', returnTo: null, drillThrough: false, listScroll: 0, sourceOptions: [],
};

// The app-wide list cap (core/listExpand.js). Leads used to keep its own 100 here — the
// one page with a cap, and a different one from everywhere else. Aliased rather than
// inlined so the five filter-reset sites below keep reading as "back to the first page".
const firstPage = () => (state.pageSize === 'ALL' ? Number.MAX_SAFE_INTEGER : Number(state.pageSize) || LIST_CAP);
const STATUSES = ['NEW', 'WORKING', 'QUALIFIED', 'UNQUALIFIED', 'CONVERTED', 'EXPORTED'];
const STATUS_HELP = {
  NEW: 'Not yet marked as in progress here. Someone may have followed up elsewhere.',
  WORKING: 'Marked as in progress by your team.',
  QUALIFIED: 'Marked as a good fit by your team.',
  UNQUALIFIED: 'Marked as not a fit by your team.',
  CONVERTED: 'Marked as a successful outcome by your team. This does not confirm a booking or payment.',
  EXPORTED: 'Marked as exported. This does not confirm CRM delivery or follow-up.',
};
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const toast = (message, type = 'info') => { try { PubSub.publish(AppBroadcast.UI_TOAST, { message, type }); } catch { /* no-op */ } };
const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
const fullName = (l) => l.lead_source === 'Google Ads call' && !l.phone && !l.email ? 'Caller not identified' : [l.first_name, l.last_name].filter(Boolean).join(' ').trim() || l.email || l.phone || 'Contact not identified';
// Letters/digits only: 'Caller (647)' must never render a 'C(' avatar.
const initials = (l) => (fullName(l).split(/\s+/)
  .map((w) => w.replace(/[^A-Za-z0-9]/g, ''))
  .filter(Boolean).slice(0, 2).map((w) => w[0]).join('') || '?').toUpperCase();

// ── One inbox, many sources ──────────────────────────────────────────────────
// lead_source is the arrival channel. The Source column shows the MAGNET'S
// NAME when a magnet earned the lead, and a plain source label otherwise -
// 'Google Ads call' sitting under a MAGNET header read as nonsense. Labels
// stay short and literal (house microcopy rule).
const SOURCE_LABEL = { 'Google Ads call': 'Ads call', 'Google LSA': 'Local Services' };
const sourceOf = (l) => state.magnetNames[l.magnet_guid] || SOURCE_LABEL[l.lead_source] || l.lead_source || '—';
// Call-shaped sources get a call glyph instead of initials torn from a
// synthetic name; leads with real people keep their initials.
const AVATAR_ICON = { 'Google Ads call': 'call' };
const avatarHTML = (l) => {
  const icon = !l.magnet_guid && AVATAR_ICON[l.lead_source];
  return icon
    ? `<span class="material-symbols-outlined" style="font-size:18px" aria-hidden="true">${icon}</span>`
    : esc(initials(l));
};
// captured_at is when the LEAD happened; created_tz is when the ROW landed.
// Identical for live magnet capture, very different for polled backfills (an
// ad call from Sep 1 ingested today must not read "2m ago" with a fresh dot).
const capturedTz = (l) => l.captured_at || l.created?.created_tz || '';
function when(l) {
  const tz = capturedTz(l);
  if (!tz) return '';
  try { return new Date(tz).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; }
}
// Speed to lead is the product's whole story — show freshness, not just a date.
function whenRel(l) {
  const tz = capturedTz(l);
  if (!tz) return '';
  try {
    const ms = Date.now() - new Date(tz).getTime();
    if (ms < 0) return when(l);
    const h = ms / 3600000;
    if (h < 1) return `${Math.max(1, Math.floor(ms / 60000))}m ago`;
    if (h < 24) return `${Math.floor(h)}h ago`;
    if (h < 48) return 'Yesterday';
    return when(l);
  } catch { return when(l); }
}
const isFresh = (l) => {
  const tz = capturedTz(l);
  if (!tz) return false;
  try { return (Date.now() - new Date(tz).getTime()) < 24 * 3600000; } catch { return false; }
};

function ensureStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-leads-styles';
  style.textContent = `
    /* The slot reserves the chip's exact height at first paint - the chip
       loads after the list, and content that appears late must fill space
       that already exists, never push the page down. Empty states (read-only
       viewer, no CRM, fetch failure) keep the same quiet band. */
    .page-shell #leads-crm-slot { min-height: 27px; margin-top: 8px; }
    .page-shell .leads-crm-chip { display: inline-flex; align-items: center; gap: 6px; height: 27px; font-size: 0.78rem; font-weight: 600; padding: 0 11px; border-radius: 999px; text-decoration: none; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-surface, #F4F7FB); color: var(--color-text-muted, #4A5A78); }
    .page-shell .leads-crm-chip:hover { border-color: var(--color-primary-600, #0072BA); color: var(--color-accent-hover, #00558D); }
    .page-shell .leads-crm-chip .material-symbols-outlined { font-size: 15px; }
    .page-shell .leads-crm-chip__dom { font-weight: 500; color: var(--color-text-subtle, #6C7C9A); margin-left: 4px; }
    .page-shell .leads-crm-chip.warn { border-color: var(--color-warning, #F5DF4D); background: var(--color-warning-soft, #FEF3C7); color: var(--color-warning-text, #92400E); }
    .page-shell .leads-crm-chip.ghost { border-style: dashed; background: transparent; }
    .page-shell .leads-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
    .page-shell .leads-filter { display: inline-flex; gap: 4px; background: var(--color-surface, #F4F7FB); border-radius: 999px; padding: 3px; }
    .page-shell .leads-filter button { appearance: none; border: 0; background: transparent; cursor: pointer; font: inherit; font-size: 0.78rem; font-weight: 600; padding: 6px 13px; border-radius: 999px; color: var(--color-text-muted, #4A5A78); }
    .page-shell .leads-filter button.active { background: var(--color-bg, #fff); color: var(--color-accent-hover, #00558D); box-shadow: 0 1px 2px rgba(11,18,32,0.12); }
    .page-shell .leads-filter .leads-pillnum { font-weight: 500; opacity: 0.65; margin-left: 3px; }
    .page-shell .leads-clientsel { font: inherit; font-size: 0.8rem; font-weight: 600; padding: 7px 10px; border-radius: 10px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); cursor: pointer; max-width: 220px; }
    .page-shell .leads-searchwrap { position: relative; margin-left: auto; }
    .page-shell .leads-searchwrap .material-symbols-outlined { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); font-size: 17px; color: var(--color-text-subtle, #6C7C9A); pointer-events: none; }
    .page-shell .leads-search { font: inherit; font-size: 0.84rem; padding: 7px 12px 7px 32px; border-radius: 10px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); width: 230px; }
    .page-shell .leads-search:focus { outline: none; border-color: var(--color-primary-600, #0072BA); box-shadow: 0 0 0 3px var(--color-accent-soft, rgba(0,114,186,0.12)); }
    .page-shell .leads-count { font-size: 0.82rem; color: var(--color-text-subtle, #6C7C9A); white-space: nowrap; }
    /* Day filter chip: the ONLY way out of a trend-chart drill-through, so it reads as
       a removable token rather than a label. */
    .page-shell .leads-daychip { display: inline-flex; align-items: center; gap: 6px; appearance: none; cursor: pointer; font: inherit; font-size: 0.78rem; font-weight: 600; white-space: nowrap; padding: 6px 10px; border-radius: 999px; border: 1px solid var(--color-primary-600, #0072BA); background: var(--color-accent-soft, rgba(0,114,186,0.1)); color: var(--color-accent-hover, #00558D); }
    .page-shell .leads-daychip:hover { background: var(--color-primary-600, #0072BA); color: #fff; }
    .page-shell .leads-daychip .material-symbols-outlined { font-size: 16px; }
    .page-shell .leads-daychip__x { opacity: 0.7; }
    .page-shell .leads-daychip:hover .leads-daychip__x { opacity: 1; }

    .page-shell .leads-tablewrap { overflow-x: auto; }
    .page-shell .leads-table { width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 0.88rem; min-width: 1000px; }
    /* Book mode adds the Client column, so the same fixed 300px of Status/Captured/
       actions has to fit alongside 82% of flexible columns instead of 70%. Its own
       floor keeps those three from being squeezed below their content. */
    .page-shell .leads-table--book { min-width: 1200px; }
    .page-shell .leads-table th { text-align: left; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); padding: 8px 12px; border-bottom: 1px solid var(--color-border, #D4DCE8); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .page-shell .leads-table th.num, .page-shell .leads-table td.num { text-align: right; }
    /* Sortable headers. The <th> keeps its own padding/typography (above); the button
       inherits all of it and adds only the affordance, so a sortable column and a plain
       one line up exactly rather than the sortable ones sitting a pixel off. */
    .page-shell .leads-table th.leads-th--sort { padding: 0; }
    .page-shell .leads-sortbtn {
      display: inline-flex; align-items: center; gap: 4px; width: 100%;
      padding: 8px 12px; appearance: none; background: none; border: 0;
      font: inherit; color: inherit; letter-spacing: inherit; text-transform: inherit;
      cursor: pointer; white-space: nowrap;
    }
    .page-shell .leads-table th.num .leads-sortbtn { justify-content: flex-end; }
    .page-shell .leads-sortbtn:hover { color: var(--color-text, #0B1220); }
    .page-shell .leads-sortbtn:focus-visible { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: -2px; }
    .page-shell .leads-table th.is-sorted .leads-sortbtn { color: var(--color-primary-600, #0072BA); }
    .page-shell .leads-sortarrow { font-size: 15px; line-height: 1; }
    /* The neutral "this is sortable" glyph stays quiet until you go looking for it;
       the active direction arrow does not. Without this the header row reads as six
       competing arrows and the one that matters is lost among them. */
    .page-shell .leads-table th:not(.is-sorted) .leads-sortarrow { opacity: 0; transition: opacity 0.12s ease; }
    .page-shell .leads-table th:not(.is-sorted):hover .leads-sortarrow,
    .page-shell .leads-table th:not(.is-sorted):focus-within .leads-sortarrow { opacity: 0.55; }
    html.ff-no-animations .page-shell .leads-table th:not(.is-sorted) .leads-sortarrow { transition: none; }
    @media (prefers-reduced-motion: reduce) { .page-shell .leads-table th:not(.is-sorted) .leads-sortarrow { transition: none; } }
    /* NOTE: text-overflow here is inert and kept only because removing it invites
       someone to "restore" it. Ellipsis needs a single line, and this rule sets no
       white-space:nowrap — real truncation is done by the inner spans (.leads-name,
       .leads-sub, a.leads-contactlink), which each set overflow+ellipsis+nowrap
       themselves. overflow:hidden IS load-bearing: it keeps a cell's content inside
       its fixed-layout column. That combination is also the trap behind the Status
       clipping — a cell squeezed under its content clips silently here rather than
       widening the table, so the wrapper's overflow-x never gets anything to scroll.
       Size columns to fit (see colgroup below) rather than relying on this to cope. */
    .page-shell .leads-table td { padding: 10px 12px; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); vertical-align: middle; overflow: hidden; text-overflow: ellipsis; }
    .page-shell .leads-row { cursor: pointer; }
    .page-shell .leads-row:hover td { background: var(--color-surface, #F7FAFD); }
    .page-shell .leads-row:focus-visible { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: -2px; }
    .page-shell .leads-open { text-align: right; }
    .page-shell .leads-open .material-symbols-outlined { font-size: 20px; color: var(--color-border, #D4DCE8); vertical-align: middle; transition: color .12s ease, transform .12s ease; }
    .page-shell .leads-row:hover .leads-open .material-symbols-outlined { color: var(--color-accent, #0072BA); transform: translateX(2px); }
    .page-shell .leads-who { display: flex; align-items: center; gap: 10px; min-width: 0; }
    .page-shell .leads-who > span:last-child { min-width: 0; }
    .page-shell .leads-avatar { flex: 0 0 auto; width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; font-weight: 700; font-size: 0.72rem; color: #fff; background: var(--color-primary-600, #0072BA); }
    .page-shell .leads-name { font-weight: 600; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell .leads-sub { font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell .leads-muted { color: var(--color-text-subtle, #6C7C9A); }
    .page-shell a.leads-contactlink { color: inherit; text-decoration: none; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell a.leads-contactlink:hover { color: var(--color-accent, #0072BA); text-decoration: underline; }
    .page-shell .leads-fresh { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: var(--color-success, #009473); margin-right: 6px; vertical-align: 1px; }
    .page-shell .leads-status { font: inherit; font-size: 0.76rem; font-weight: 600; padding: 5px 8px; border-radius: 8px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); cursor: pointer; max-width: 100%; }
    /* nowrap so a single long status ("UNQUALIFIED") can never break mid-word inside
       a tight cell — td has overflow:hidden, so a wrap there reads as truncation with
       no scrollbar to reach the rest. */
    .page-shell .leads-badge { display: inline-block; white-space: nowrap; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.03em; padding: 3px 9px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); }
    .page-shell .leads-badge[data-s="NEW"] { background: #DBEAFE; color: #1D4ED8; }
    .page-shell .leads-badge[data-s="WORKING"] { background: #FEF3C7; color: #92400E; }
    .page-shell .leads-badge[data-s="QUALIFIED"], .page-shell .leads-badge[data-s="CONVERTED"] { background: #DCFCE7; color: #047857; }

    /* Two-pane layout: table left, detail panel right — but the panel column only exists
       once a lead is actually selected.
       It used to reserve its 360px permanently while holding nothing but "Click to see
       lead information", and because the table in the left column has min-width 1000px
       (1200px in book mode), that reserved strip is exactly what pushed the table past
       the viewport and produced a horizontal scrollbar on the default view. The space was
       being spent to display a prompt.
       Driven by [data-has-selection] on the layout element, so the grid owns the widths
       rather than JS; the gap collapses with the column so there is no phantom 24px left
       behind either. */
    .page-shell .leads-layout { display: grid; grid-template-columns: minmax(0, 1fr); gap: 0; align-items: start; }
    .page-shell .leads-layout[data-has-selection="true"] { grid-template-columns: minmax(0, 1fr) 360px; gap: 24px; }
    .page-shell .leads-layout:not([data-has-selection="true"]) .leads-panel { display: none; }
    /* Clears the sticky sub-nav instead of guessing 24px from the viewport top. */
    /* Sticks to the SCROLL PANE (#app), whose top already sits below the contextual top
       bar — so this is a plain gap, not subnav-height plus a gap as it was when the whole
       document scrolled and the panel had to clear that bar itself. */
    .page-shell .leads-panel { position: sticky; top: 24px; max-height: calc(100dvh - var(--subnav-height, 61px) - 48px); overflow-y: auto; }
    @media (max-width: 1180px) {
      .page-shell .leads-layout[data-has-selection="true"] { grid-template-columns: 1fr; }
      .page-shell .leads-panel { position: static; max-height: none; }
    }
    .page-shell .leads-row.selected td { background: var(--color-accent-soft, #EEF4FF); }
    .page-shell .leads-row.selected .leads-open .material-symbols-outlined { color: var(--color-accent, #0072BA); }

    .page-shell .leads-det__head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
    .page-shell .leads-det__ava { width: 42px; height: 42px; font-size: 0.9rem; }
    .page-shell .leads-det__name { font-family: var(--font-display, inherit); font-size: 1.05rem; font-weight: 700; margin-bottom: 4px; }
    .page-shell .leads-det__contact { display: flex; gap: 8px; margin: 4px 0 14px; flex-wrap: wrap; }
    .page-shell .leads-det__cbtn { display: inline-flex; align-items: center; gap: 6px; text-decoration: none; font-size: 0.82rem; font-weight: 600; padding: 8px 12px; border-radius: 10px; border: 1px solid var(--color-border, #D4DCE8); color: var(--color-accent-hover, #00558D); }
    .page-shell .leads-det__cbtn:hover { background: var(--color-accent-soft, #EEF4FF); }
    .page-shell .leads-det__cbtn .material-symbols-outlined { font-size: 17px; }
    .page-shell .leads-det__field { padding: 9px 0; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); display: flex; gap: 12px; }
    .page-shell .leads-det__k { flex: 0 0 96px; font-size: 0.78rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .leads-det__v { flex: 1; font-size: 0.86rem; word-break: break-word; }
    .page-shell .leads-det__notes { white-space: pre-wrap; overflow-wrap: anywhere; font-size: 0.84rem; line-height: 1.5; color: var(--color-text-muted, #4A5A78); background: var(--color-surface, #F4F7FB); border-radius: 10px; padding: 12px 14px; margin-top: 14px; }
    .page-shell .leads-det__history { margin-top: 14px; color: var(--color-text-muted, #4A5A78); font-size: 0.84rem; }
    .page-shell .leads-det__history summary { cursor: pointer; font-weight: 600; }
    /* Destructive action sits below everything else and behind a divider, so it is
       never adjacent to the routine controls (status, contact) a user clicks often. */
    .page-shell .leads-det__actions { margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--color-surface-2, #EAF0F7); }
    .page-shell .leads-det__del { display: inline-flex; align-items: center; gap: 6px; appearance: none; cursor: pointer; font: inherit; font-size: 0.82rem; font-weight: 600; padding: 7px 12px; border-radius: 9px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); color: var(--color-danger, #DD4124); }
    .page-shell .leads-det__del:hover:not(:disabled) { background: var(--color-danger-soft, rgba(221,65,36,0.08)); border-color: var(--color-danger, #DD4124); }
    .page-shell .leads-det__del:disabled { opacity: 0.55; cursor: default; }
    .page-shell .leads-det__del .material-symbols-outlined { font-size: 17px; }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

function emptyState(icon, msg) {
  return `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;
}

function statusSelect(l, { detail = false } = {}) {
  return `<select class="leads-status" data-id="${esc(l.id)}" data-act="status" ${detail ? 'id="leads-detail-status-select" aria-describedby="leads-status-help leads-status-effects"' : 'aria-label="Lead status"'} onclick="event.stopPropagation()">
    <optgroup label="Lead progress">${STATUSES.filter(s=>s!=='EXPORTED').map((s) => `<option value="${s}" ${l.status === s ? 'selected' : ''}>${s[0]}${s.slice(1).toLowerCase()}</option>`).join('')}</optgroup>
    ${l.status === 'EXPORTED' ? '<optgroup label="Previous status"><option value="EXPORTED" selected disabled>Exported (previous status)</option></optgroup>' : ''}
  </select>`;
}

function rowHTML(l) {
  const magnet = sourceOf(l);
  const tel = l.phone ? String(l.phone).replace(/[^\d+]/g, '') : '';
  return `
    <tr class="leads-row${String(l.id) === state.selectedId ? ' selected' : ''}" data-id="${esc(l.id)}" tabindex="0" role="button" aria-label="Open lead ${esc(fullName(l))}">
      <td>
        <span class="leads-who">
          <span class="leads-avatar">${avatarHTML(l)}</span>
          <span><span class="leads-name">${esc(fullName(l))}</span>${l.company ? `<span class="leads-sub">${esc(l.company)}</span>` : ''}</span>
        </span>
      </td>
      ${state.book ? `<td>${clientChipHTML(state.ownerNames[l.owner_guid] || '') || '<span class="leads-muted">—</span>'}</td>` : ''}
      <td>
        ${l.email ? `<a class="leads-contactlink" href="mailto:${esc(l.email)}" title="Email ${esc(l.email)}">${esc(l.email)}</a>` : '<span class="leads-muted">—</span>'}
        ${l.phone ? `<a class="leads-contactlink leads-sub" href="tel:${esc(tel)}" title="Call ${esc(l.phone)}">${esc(l.phone)}</a>` : ''}
      </td>
      <td>${l.product_interest ? esc(l.product_interest) : '<span class="leads-muted">—</span>'}</td>
      <td><span class="leads-sub">${esc(magnet)}</span>${(l.utm_campaign || l.utm_source) ? `<span class="leads-sub" title="Campaign / source">${esc(l.utm_campaign || l.utm_source)}${l.gclid ? ' <span class="leads-badge" data-s="QUALIFIED" style="font-size:0.58rem;padding:1px 6px">Ads</span>' : ''}</span>` : ''}</td>
      <td>${canManageLead(l) ? statusSelect(l) : `<span class="leads-badge" data-s="${esc(l.status)}">${esc(l.status)}</span>`}</td>
      <td class="num leads-muted" style="white-space:nowrap" title="${esc(when(l))}">${isFresh(l) ? '<span class="leads-fresh" title="Captured in the last 24 hours"></span>' : ''}${esc(whenRel(l))}</td>
      <td class="leads-open" aria-hidden="true"><span class="material-symbols-outlined">chevron_right</span></td>
    </tr>`;
}

const clientPass = (l) => state.clientFilter === 'ALL' || l.owner_guid === state.clientFilter;
function searchPass(l) {
  const q = state.search.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    l.first_name, l.last_name, l.email, l.company, l.phone, l.product_interest,
    state.magnetNames[l.magnet_guid], state.book ? state.ownerNames[l.owner_guid] : '',
    // Attribution was searchable nowhere despite being rendered in the table (next to
    // the magnet name) and in the detail drawer, so typing a campaign you could see
    // on screen returned nothing. Also what makes the dashboard's Top Campaigns
    // drill-through work — it lands here by pre-filling this same search.
    l.utm_campaign, l.utm_source,
  ].filter(Boolean).join(' ').toLowerCase();
  return q.split(/\s+/).every((w) => hay.includes(w));
}
// Bucketed on the UTC date, NOT the viewer's local one. That is deliberate: the
// dashboard asks for its window with toISOString().slice(0,10) and the chart's columns
// are keyed the same way, so matching on the local date would put a lead in a
// different bucket than the column that was clicked. The consequence to know about is
// that a lead captured late in the evening west of UTC belongs to the next day here,
// which is consistent with the chart but not with the row's own displayed date (that
// one is localised). Chart and filter agreeing matters more.
const capturedDay = l => { const date = new Date(capturedTz(l)); return Number.isFinite(date.getTime()) ? date.toISOString().slice(0, 10) : ''; };
const dayPass = (l) => !state.day || capturedDay(l) === state.day;

const magnetPass = (l) => state.magnetFilter === 'ALL' || l.magnet_guid === state.magnetFilter;
// 'MAGNET' groups every magnet-captured lead; any other value is an exact
// lead_source (Google Ads call, Google LSA, ...) - the same field the
// product stats split on, so the filter and the numbers can never disagree.
const sourcePass = (l) => state.sourceFilter === 'ALL'
  || (state.sourceFilter === 'MAGNET' ? l.lead_source === 'Magnet' : l.lead_source === state.sourceFilter);

/**
 * Standing date ranges. Until now the only time filter was `day`, a single date you
 * could not choose from this page at all — it arrives from a dashboard drill-through.
 * "Leads from the last 30 days" had no control anywhere.
 *
 * Bucketed on the UTC day like dayPass(), for the same reason: the dashboard's windows
 * and the trend chart's columns are keyed that way, so a local-date boundary here would
 * disagree with the numbers the user clicked through from.
 */
const RANGES = { ALL: null, 7: 7, 30: 30, 90: 90 };
const RANGE_LABEL = { ALL: 'All time', 7: 'Last 7 days', 30: 'Last 30 days', 90: 'Last 90 days' };

function rangePass(l) {
  const day = capturedDay(l);
  if (state.from || state.to) return !!day && (!state.from || day >= state.from) && (!state.to || day <= state.to);
  const days = RANGES[state.range];
  if (!days) return true;
  if (!day) return false;               // undated leads cannot satisfy a date window
  // Inclusive of today: "last 7 days" means today plus the six before it, which is what
  // the dashboard's own 7-day window covers.
  const cutoff = new Date(Date.now() - (days - 1) * 86400000).toISOString().slice(0, 10);
  return day >= cutoff && day <= new Date().toISOString().slice(0, 10);
}

function filtered() {
  return state.leads.filter((l) =>
    (state.filter === 'ALL' || l.status === state.filter)
    && (!state.leadId || String(l.id) === state.leadId) && (!state.leadGuid || l.guid === state.leadGuid)
    && clientPass(l) && magnetPass(l) && sourcePass(l) && searchPass(l) && dayPass(l) && rangePass(l));
}

/**
 * Sortable columns, keyed by what the cell actually DISPLAYS.
 *
 * Sorting by a hidden field is the classic version of this bug: sorting "Name" by
 * last_name when the column renders "first last" looks broken to the person reading it.
 * So Name sorts on fullName() — the same helper the cell uses, including its
 * email/(no name) fallbacks — and Contact sorts on the email that heads the cell, not
 * the phone underneath it.
 *
 * `dir` is each column's FIRST click direction: alphabetical columns open ascending
 * (A first), the date opens descending (newest first), because that is what someone
 * clicking them is asking for. Clicking again reverses.
 */
const SORTS = {
  name: { label: 'Name', dir: 'asc', value: (l) => fullName(l).toLowerCase() },
  contact: { label: 'Contact', dir: 'asc', value: (l) => String(l.email || l.phone || '').toLowerCase() },
  // ISO-8601 UTC strings compare lexicographically in chronological order, so no Date
  // parsing per comparison. Missing timestamps sort as '' — see the blank handling below.
  captured: { label: 'Captured', dir: 'desc', value: (l) => capturedTz(l) },
};

/**
 * Filter first, THEN sort, so the order applies to what is actually on screen: filter to
 * Qualified and sort by Captured and you get qualified leads in date order. Sorting the
 * whole set first would produce the same rows, but any later "sort within this view"
 * behaviour would have to be re-derived — doing it in this order keeps one pipeline:
 * filter -> sort -> slice to state.shown.
 */
function sortedFiltered() {
  if (state.workspace) return state.leads;
  const rows = filtered();
  const spec = SORTS[state.sort.key];
  if (!spec) return rows;
  const dir = state.sort.dir === 'asc' ? 1 : -1;
  // Not sorting state.leads in place: it is the fetched set, and mutating it would make
  // the order depend on how many times the user had clicked a header.
  return rows.slice().sort((a, b) => {
    const av = spec.value(a);
    const bv = spec.value(b);
    // Blanks last in BOTH directions. A lead with no email is not "before A" or "after
    // Z" — it has no place in the order, and letting it lead the list on one click and
    // trail it on the next reads as the sort being broken.
    if (av === '' && bv === '') return 0;
    if (av === '') return 1;
    if (bv === '') return -1;
    if (av === bv) return 0;
    return av < bv ? -dir : dir;
  });
}

/** One sortable header cell, carrying its own state for assistive tech and for CSS. */
function thSortHTML(key, extraClass = '') {
  const spec = SORTS[key];
  const active = state.sort.key === key;
  const dir = active ? state.sort.dir : '';
  // aria-sort is the property screen readers announce; the arrow is the sighted
  // equivalent. Both come from the same state so they cannot disagree.
  return `<th class="leads-th--sort${extraClass ? ` ${extraClass}` : ''}${active ? ' is-sorted' : ''}"
      aria-sort="${active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}">
      <button type="button" class="leads-sortbtn" data-sort="${key}"
        title="Sort by ${esc(spec.label)}${active ? (dir === 'asc' ? ' (descending)' : ' (ascending)') : ''}">
        ${esc(spec.label)}<span class="leads-sortarrow material-symbols-outlined" aria-hidden="true">${active ? (dir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}</span>
      </button>
    </th>`;
}

/** Click a header: same column reverses, a new column starts at its natural direction. */
function applySort(key) {
  if (!SORTS[key]) return false;
  if (state.sort.key === key) {
    state.sort.dir = state.sort.dir === 'asc' ? 'desc' : 'asc';
  } else {
    state.sort = { key, dir: SORTS[key].dir };
  }
  // Back to the first page: leaving the reader 200 rows deep into a list that just
  // reordered under them shows a slice of the middle of the new order.
  state.shown = firstPage();
  return true;
}

// The chip is the only way back out of a day filter, so it is never silently applied
// without one being rendered — see renderBody.
function dayChipHTML() {
  if (!state.day) return '';
  let label = state.day;
  try {
    label = new Date(`${state.day}T00:00:00Z`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  } catch { /* fall back to the raw ISO day */ }
  return `<button class="leads-daychip" type="button" id="leads-dayclear" title="Show every day again">
    <span class="material-symbols-outlined" aria-hidden="true">event</span>${esc(label)}
    <span class="material-symbols-outlined leads-daychip__x" aria-hidden="true">close</span>
  </button>`;
}

const panelEmpty = () => emptyState('touch_app', 'Click to see lead information.');

export function render() {
  ensureStyles(); ensurePortfolioStyles();
  return `<div class="page-shell leads-workspace" data-section="leads">
    <div class="page-header"><div class="page-header-text">
      <div id="leads-return"></div><h1 class="page-title">Leads</h1>
      ${inPortfolio() ? '<p class="page-description">Across your clients</p>' : ''}
    </div><button class="btn btn-secondary" id="leads-export" type="button" hidden>Export CSV</button></div>
    ${inPortfolio() ? '' : '<details class="leads-connections"><summary>CRM connections <span id="leads-connection-health" role="status"></span></summary><p>Manage where inquiries are sent. A connected CRM does not confirm delivery of each inquiry.</p><div id="leads-crm-slot"></div><a href="#/settings/integrations">View connections →</a></details>'}
    <div id="leads-notice" role="status"></div>
    <div class="leads-layout" data-has-selection="false">
      <div class="dashboard-card leads-list-card"><div class="card-body" id="leads-body">${loadingState('list', 'Loading leads')}</div></div>
      <section class="dashboard-card leads-panel" aria-label="Lead details"><div class="card-body" id="leads-panel-body"></div></section>
    </div></div>`;
}

const scopeKey = () => JSON.stringify(leadsHandoffScope());
function rememberView() {
  const view = { view: state.view, filter: state.filter, clientFilter: state.clientFilter,
    search: state.search, day: state.day, range: state.range, magnetFilter: state.magnetFilter,
    sourceFilter: state.sourceFilter, from: state.from, to: state.to, offset: state.offset,
    selectedId: state.selectedId, leadId: state.leadId, leadGuid: state.leadGuid, returnTo: state.returnTo, drillThrough: state.drillThrough,
    sort: { ...state.sort }, listScroll: state.listScroll || 0, scroll: Math.round(document.getElementById('app')?.scrollTop || 0) };
  viewMemory.set(currentScope, view);
  if (scopeKey() === currentScope) saveLeadView(view);
  if (!state.drillThrough && !state.returnTo) {
    const listView = { ...view, selectedId: null, leadId: '', leadGuid: '', returnTo: null,
      scroll: state.selectedId ? view.listScroll : view.scroll };
    listViewMemory.set(currentScope, listView);
    if (scopeKey() === currentScope) saveLeadListView(listView);
  }
}
export async function init() {
  clearLeadBootRoute();
  const nextScope = scopeKey();
  const directVisit = takeLeadListVisit();
  const restoredView = directVisit
    ? listViewMemory.get(nextScope) || readLeadListView()
    : viewMemory.get(nextScope) || readLeadView();
  inquiryHistory?.destroy(); inquiryHistory = null;
  if (nextScope !== currentScope || directVisit) {
    currentScope = nextScope;
    Object.assign(state, { leads: [], magnetNames: {}, ownerNames: {}, selectedId: null, filter: 'ALL',
      clientFilter: 'ALL', search: '', day: '', range: '30', magnetFilter: 'ALL', sourceFilter: 'ALL',
      from: '', to: '', offset: 0, leadId: '', leadGuid: '', returnTo: null, drillThrough: false, listScroll: 0, workspace: null,
      sourceOptions: [], canManage: false, view: 'compact', sort: { key: 'captured', dir: 'desc' } }, restoredView || {});
  }
  document.querySelector('.leads-workspace')?.addEventListener('click', event => {
    const href = event.target.closest('a')?.getAttribute('href') || '';
    if (!/^#\/settings\/integrations(?:\/[\w-]+)?$/.test(href)
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || scopeKey() !== currentScope) return;
    rememberView(); rememberLeadConnectionReturn();
  });
  document.getElementById('leads-export')?.addEventListener('click', exportCsv);
  planAllowsClient('csv_export').then(allowed => {
    if (!allowed && currentScope === nextScope) document.getElementById('leads-export')?.remove();
  }).catch(() => {});
  // Claim old handoffs even on direct entry so an abandoned report cannot leak
  // into a later visit. Only an intentional drill-through may apply them.
  const claimedContext = takeLeadsHandoff(leadsHandoffScope());
  const claimedSearch = takeHandoff(HANDOFF.LEADS_SEARCH);
  const claimedDay = takeHandoff(HANDOFF.LEADS_DAY);
  const context = directVisit ? null : claimedContext;
  const pendingSearch = directVisit ? null : claimedSearch;
  const pendingDay = directVisit ? null : claimedDay;
  if (context || pendingSearch || pendingDay) {
    Object.assign(state, { search: context?.search || pendingSearch || '', day: pendingDay || '', filter: context?.status || 'ALL',
      clientFilter: context?.ownerGuid || 'ALL', magnetFilter: context?.magnetGuid || 'ALL', sourceFilter: context?.source || 'ALL',
      range: 'ALL', shown: firstPage(), offset: 0, selectedId: context?.leadId ? String(context.leadId) : null,
      leadId: context?.leadId || '', leadGuid: context?.leadGuid || '', from: context?.from || '',
      to: context?.to || '', returnTo: context?.returnTo || null, drillThrough: true });
  }
  const back = document.getElementById('leads-return');
  if (back && state.returnTo) {
    back.innerHTML = `<a href="${esc(state.returnTo.hash)}">← ${esc(state.returnTo.label)}</a>`;
    back.querySelector('a').addEventListener('click', () => {
      rememberView();
      const guid = state.returnTo.hash.match(/^#\/magnets\/([^/]+)\/results$/)?.[1];
      if (guid && state.from && state.to) setMagnetResultsHandoff({ ...leadsHandoffScope(), magnetGuid: decodeURIComponent(guid), from: state.from, to: state.to });
    });
  }
  await load();
  const scroll = restoredView?.scroll;
  if (!context && scroll && document.getElementById('app')) document.getElementById('app').scrollTop = scroll;
  rememberView();
  loadCrmChip();
}

function queryParams(limit = 25, offset = state.offset) {
  const params = new URLSearchParams({ workspace: '1', ...(limit === 25 ? { activity: '1' } : {}), limit: String(limit), offset: String(offset), sort: state.sort.key, dir: state.sort.dir });
  for (const [key, value] of Object.entries({ q: state.search.trim(), status: state.filter, source: state.sourceFilter,
    magnet_guid: state.magnetFilter, owner_guid: state.clientFilter, lead_id: state.leadId, lead_guid: state.leadGuid })) {
    if (value && value !== 'ALL') params.set(key, value);
  }
  let from = state.from, to = state.to;
  if (state.day) from = to = state.day;
  else if (RANGES[state.range]) {
    from = new Date(Date.now() - (RANGES[state.range] - 1) * 86400000).toISOString().slice(0, 10);
    to = new Date().toISOString().slice(0, 10);
  }
  if (from) params.set('from', from); if (to) params.set('to', to);
  return params;
}
function notice(message) { const el = document.getElementById('leads-notice'); if (el) el.textContent = message; }
async function refresh() {
  clearTimeout(searchTimer); state.offset = 0; state.selectedId = null; state.shown = firstPage();
  rememberView();
  if (state.workspace) await load(); else renderBody();
}

// Workspace connection health is separate from individual delivery receipts.
// Surface exceptions in the closed summary, including unavailable health.
const CRM_SLUGS = ['hubspot', 'zoho', 'salesforce', 'gohighlevel'];
function updateConnectionsTool() {
  const button = document.querySelector('[data-record-tool="connections"]');
  const warning = document.getElementById('leads-connection-health')?.classList.contains('needs-attention') || false;
  button?.classList.toggle('needs-attention', warning);
  button?.setAttribute('aria-label', warning ? 'Connections: workspace needs attention' : 'Connections');
}
async function loadCrmChip() {
  const slot = document.getElementById('leads-crm-slot');
  if (!slot) return;
  const health = document.getElementById('leads-connection-health');
  const showHealth = (message, warning = false) => {
    if (!health?.isConnected) return;
    health.textContent = message; health.classList.toggle('needs-attention', warning);
    updateConnectionsTool();
  };
  const scope = currentScope;
  try {
    const { denied, providers, configured } = await fetchIntegrations();
    if (!slot.isConnected || scope !== currentScope || scopeKey() !== scope) return;
    if (denied) { slot.textContent = 'Connection details are not available with your access.'; showHealth('Details unavailable'); return; }
    const states = CRM_SLUGS.map(slug => {
      const p = INTG_PROVIDERS[slug], owner = AuthController.getActiveOrg()?.guid;
      const conn = owner ? (providers?.[slug] || []).find(item => item.owner_guid === owner) : connFor(providers, slug);
      return { p, conn, status: statusOf(p, conn, !!configured?.[slug]) };
    });
    const active = states.filter(s => ['connected', 'broken'].includes(s.status));
    const broken = active.filter(s => s.status === 'broken');
    showHealth(broken.length ? `${broken.map(s => s.p.label).join(', ')} needs reconnecting` : '', broken.length > 0);
    slot.innerHTML = active.map(s => `<a class="leads-crm-chip ${s.status === 'broken' ? 'warn' : ''}" href="#/settings/integrations/${s.p.urlSlug}">${esc(s.p.label)} ${s.status === 'broken' ? 'needs reconnecting' : 'connected'}${s.conn?.external_account?.hub_domain ? `<span class="leads-crm-chip__dom">${esc(s.conn.external_account.hub_domain)}</span>` : ''}</a>`).join(' ');
    if (!active.length) slot.innerHTML = '<p>No CRM is connected to this workspace.</p>' + ((AuthController.getCapabilities?.() || []).includes('manage_magnets') ? '<a class="leads-crm-chip ghost" href="#/settings/integrations">Connect your CRM</a>' : '');
  } catch { if (slot.isConnected && scope === currentScope && scopeKey() === scope) {
    slot.textContent = 'Connection details could not be loaded. Open Connections to check.';
    showHealth('Could not check connections', true);
  } }
}

async function load() {
  const body = document.getElementById('leads-body'); if (!body) return;
  const ticket = ++loadEpoch, scope = currentScope;
  const live = () => ticket === loadEpoch && body.isConnected && scope === currentScope && scopeKey() === scope;
  const finishRefresh = beginRefresh(body, 'Updating leads');
  body.setAttribute('aria-busy', 'true');
  const exportButton = document.getElementById('leads-export');
  if (exportButton) exportButton.disabled = true;
  try {
    const [lres, mres] = await Promise.all([
      guardedFetch(`${apiBase()}/m5t/v5/m5mLead?${queryParams()}`),
      guardedFetch(`${apiBase()}/m5t/v5/magnets`).catch(() => null),
    ]);
    let ldata = await lres.json().catch(() => ({}));
    // Older services ignore workspace filters. Fetch their full bounded legacy page
    // before enabling local filtering, and disclose any remaining truncation.
    if (lres.ok && ldata.ok && ldata.workspace?.version !== 1 && ldata.total > (ldata.data || []).length) {
      const legacyRes = await guardedFetch(`${apiBase()}/m5t/v5/m5mLead?limit=1000`);
      const legacyData = await legacyRes.json(); failIfNotOk(legacyRes, legacyData);
      if (!legacyData.ok) throw new Error('Failed to load legacy records'); ldata = legacyData;
    }
    const mdata = mres ? await mres.json().catch(() => ({})) : null;
    if (!live()) return;
    failIfNotOk(lres, ldata);
    if (!ldata.ok) throw new Error('Failed to load');
    state.leads = (ldata.data || []).filter(l => !['DELETED', 'ARCHIVED'].includes(l.status));
    state.workspace = ldata.workspace?.version === 1 ? ldata.workspace : null;
    state.total = Number.isFinite(ldata.total) ? ldata.total : state.leads.length;
    state.truncated = !state.workspace && state.total > state.leads.length;
    state.book = inPortfolio(); state.ownerNames = state.book ? clientNameMap() : {};
    state.canManage = !!(mres?.ok && mdata?.canManage);
    state.magnetNames = {};
    (mdata?.magnets || []).forEach(m => { state.magnetNames[m.guid] = m.name;
      if (m.owner_guid && m.owner_name) state.ownerNames[m.owner_guid] = m.owner_name; });
    state.sourceOptions = [...new Set([...state.sourceOptions, ...(state.workspace?.source_counts || []).map(s => s.source), ...state.leads.map(l => l.lead_source)].filter(Boolean))];
    if (state.leadGuid) state.selectedId = String(state.leads.find(l => l.guid === state.leadGuid)?.id || '') || null;
    if (state.selectedId && !state.leads.some(l => String(l.id) === state.selectedId)) state.selectedId = null;
    if (state.offset >= state.total && state.offset > 0) { state.offset = Math.max(0, Math.floor((state.total - 1) / 25) * 25); return load(); }
    renderBody();
  } catch (err) {
    if (!live()) return;
    inquiryHistory?.destroy(); state.selectedId = null; renderPanel();
    if (exportButton) exportButton.hidden = true;
    body.innerHTML = isDenial(err) ? denialState() : `<section class="leads-empty leads-load-error" role="alert"><h2>Could not load your leads</h2><p>Your filters are kept. Try loading them again.</p><button type="button" id="leads-retry">Try again</button></section>`;
    body.querySelector('#leads-retry')?.addEventListener('click', async () => {
      await load();
      if (body.isConnected && scope === currentScope && scopeKey() === scope) (body.querySelector('#leads-search') || body.querySelector('#leads-retry'))?.focus({ preventScroll: true });
    });
  } finally { finishRefresh(); if (live()) body.removeAttribute('aria-busy'); }
}

// With table-layout:fixed these widths are authoritative, and when they total MORE
// than the table's width the browser scales every column down proportionally. That is
// what clipped Status. 120px was already tight for its widest content ("Unqualified"
// in a <select>, plus padding, border and the dropdown arrow) and the proportional
// squeeze took it under. Because td carries overflow:hidden the overflow happened
// INSIDE the cell, so the table itself never exceeded its wrapper and
// .leads-tablewrap's overflow-x:auto had nothing to scroll — hence clipped content
// with no way to reach it.
//
// Widths are now sized to FIT at the table's own min-width instead of relying on the
// squeeze staying gentle:
//   flexible 70% (22+20+13+15) + fixed 300px (150+110+40)
//   0.70 × 1000 + 300 = 1000 → exactly fills leads-table's 1000px floor.
// Book mode adds the Client column and gets its own wider floor (see the CSS below).
// Narrower than the floor, the WRAPPER scrolls — the behaviour that was missing.
const colgroup = () => `
  <colgroup>
    <col style="width:22%">
    ${state.book ? '<col style="width:12%">' : ''}
    <col style="width:20%">
    <col style="width:13%">
    <col style="width:15%">
    <col style="width:150px">
    <col style="width:110px">
    <col style="width:40px">
  </colgroup>`;

const sourceNavLabel = source => ({ Magnet: 'Website', 'Google Ads call': 'Google Ads calls', 'Google LSA': 'Local Services' }[source] || source || 'Other');
function recordDayLabel(day) {
  if (!day) return 'Date unavailable';
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  return day === today ? 'Today' : day === yesterday ? 'Yesterday'
    : new Date(`${day}T00:00:00Z`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
function compactRowHTML(l) {
  const call = callOf(l), ad = adCallOf(l), local = lsaOf(l);
  const isCall = !!(call || ad || l.lead_source === 'Google Ads call');
  const isMagnet = !!l.magnet_guid || l.lead_source === 'Magnet';
  const anonymous = ['Caller not identified', 'Contact not identified'].includes(fullName(l));
  const title = isCall && anonymous ? (ad || l.lead_source === 'Google Ads call' ? 'Google Ads call' : 'Phone call') : fullName(l);
  const summary = ad ? [ad.outcome, ad.dur].filter(Boolean).join(' · ')
    : call ? [callOutcome(call.answered), call.dur].filter(Boolean).join(' · ')
    : isCall ? 'Call recorded'
    : local ? [local.type || 'Local Services inquiry', local.category].filter(Boolean).join(' · ')
    : l.product_interest || (isMagnet ? 'Website inquiry' : 'Inquiry');
  const available = l.email || usablePhone(l);
  const date = new Date(capturedTz(l)), validDate = Number.isFinite(date.getTime());
  const time = validDate ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }) : '—';
  const stamp = validDate ? `${recordDayLabel(capturedDay(l))}, ${time} UTC` : 'Date unavailable';
  const source = isMagnet ? `Magnet · ${sourceOf(l)}` : title === 'Google Ads call' ? ad?.campaign || '' : sourceOf(l);
  return `<button type="button" class="leads-row leads-compact-row${String(l.id) === state.selectedId ? ' selected' : ''}" data-id="${esc(l.id)}" aria-label="Open lead ${esc(title)}, ${esc(summary)}, ${esc(stamp)}">
    <span class="leads-row-identity"><span class="leads-avatar${isCall ? ' leads-avatar--call' : ''}">${isCall ? '<span class="material-symbols-outlined" aria-hidden="true">call</span>' : avatarHTML(l)}</span><span class="leads-row-content"><strong>${esc(title)}</strong>
      <span class="leads-row-summary">${esc(summary)}</span></span></span>
    <span class="leads-row-source">${esc(source)}${!available ? `<span class="leads-availability">${isCall ? 'No callback number' : 'No contact details'}</span>` : ''}${state.book ? `<span class="leads-sub">${esc(state.ownerNames[l.owner_guid] || 'Client')}</span>`:''}</span>
    <span class="leads-row-meta"><span class="leads-badge" data-s="${esc(l.status)}">${esc(statusLabel(l.status))}</span></span>
    <time class="leads-row-time" ${validDate ? `datetime="${esc(date.toISOString())}"` : ''} title="${esc(stamp)}">${esc(state.sort.key === 'captured' ? time : recordDayLabel(capturedDay(l)))}</time><span class="leads-open-label" aria-hidden="true">→</span></button>`;
}
function compactListHTML(rows) {
  let previousDay;
  return rows.map(lead => {
    const day = capturedDay(lead);
    const heading = state.sort.key === 'captured' && day !== previousDay
      ? `<h2 class="leads-date-group">${esc(recordDayLabel(day))}<span>UTC</span></h2>` : '';
    previousDay = day;
    return heading + compactRowHTML(lead);
  }).join('');
}
const canManageLead = lead => state.workspace ? lead?.can_manage === true : state.canManage;
const statusLabel = status => status ? status[0] + status.slice(1).toLowerCase() : 'Status unavailable';
const usablePhone = l => String(l.phone || l.mobile || '').replace(/[^\d+]/g, '').replace(/^\+$/, '');
const callOutcome = value => value === 'yes' || value === 'true' ? 'Answered' : value === 'no' || value === 'false' ? 'Missed call' : 'Outcome unavailable';

function renderBody() {
  const body = document.getElementById('leads-body'); if (!body) return;
  const active = document.activeElement, focusId = body.contains(active) ? active.id : '', selection = active?.selectionStart;
  const exportBtn = document.getElementById('leads-export'); if (exportBtn) { exportBtn.hidden = !state.total; exportBtn.disabled = false; exportBtn.style.display = ''; }
  const options = (values, selected) => values.map(([value, label]) => `<option value="${esc(value)}"${selected === value ? ' selected' : ''}>${esc(label)}</option>`).join('');
  const filters = [['filter', state.filter, statusLabel(state.filter)], ['clientFilter', state.clientFilter, state.ownerNames[state.clientFilter]],
    ['magnetFilter', state.magnetFilter, state.magnetNames[state.magnetFilter] || 'Selected magnet'], ['sourceFilter', state.sourceFilter, state.sourceFilter === 'MAGNET' ? 'Website inquiries' : state.sourceFilter],
    ['identity', state.leadId || state.leadGuid, 'Selected record']].filter(([,v]) => v && v !== 'ALL');
  const hasFacets = state.workspace?.source_facet_scope === 'all_filters_except_source' && Array.isArray(state.workspace?.source_facets);
  const breakdown = (hasFacets ? state.workspace.source_facets : state.workspace?.source_counts) || [];
  const allCount = hasFacets ? breakdown.reduce((sum,item) => sum + item.count, 0) : state.sourceFilter === 'ALL' ? state.total : null;
  const lsaActivity = state.workspace?.lsa_activity;
  const sourceChoices = [...new Set([...state.sourceOptions, ...(lsaActivity?.rows?.length ? ['Google LSA'] : []), ...breakdown.map(s => s.source), ...(state.sourceFilter !== 'ALL' ? [state.sourceFilter === 'MAGNET' ? 'Magnet' : state.sourceFilter] : [])])]
    .sort((a,b) => a === 'Magnet' ? -1 : b === 'Magnet' ? 1 : sourceNavLabel(a).localeCompare(sourceNavLabel(b)));
  const secondaryFilterCount = filters.filter(([key]) => !state.workspace || key !== 'sourceFilter').length;
  body.innerHTML = `  ${state.workspace ? `<div class="leads-source-breakdown" aria-label="Filter records by capture source"><button type="button" id="leads-source-all" data-source="ALL" aria-pressed="${state.sourceFilter === 'ALL'}">All sources${Number.isFinite(allCount) && !lsaActivity?.calls ? ` <b>${allCount}</b>` : ''}</button>${sourceChoices.map(source => {
    const count = breakdown.find(item => item.source === source)?.count;
    const selected = state.sourceFilter === (source === 'Magnet' ? 'MAGNET' : source);
    const showCount = Number.isFinite(count) && (hasFacets || state.sourceFilter === 'ALL' || selected);
    return `<button type="button" id="leads-source-${esc(encodeURIComponent(source || 'unknown'))}" aria-pressed="${selected}" ${source ? `data-source="${esc(source)}"` : 'disabled'}><span class="material-symbols-outlined" aria-hidden="true">${source === 'Magnet' ? 'forum' : source === 'Google LSA' ? 'home_repair_service' : /call/i.test(source) ? 'call' : 'inbox'}</span>${esc(sourceNavLabel(source))}${source === 'Google LSA' && lsaActivity?.calls ? ` <b>${lsaActivity.calls}${lsaActivity.truncated ? '+' : ''} calls</b>` : showCount ? ` <b>${count}</b>` : ''}</button>`;
  }).join('')}</div>` : ''}
<div class="leads-workspace-toolbar">
    <label><span class="leads-sr">Date captured</span><select id="leads-rangesel" class="leads-clientsel">${state.from || state.to || state.day ? '<option value="custom" selected disabled>Selected dates</option>' : ''}${options(Object.entries(RANGE_LABEL), state.from || state.to || state.day ? 'custom' : state.range)}</select></label>
    <label class="leads-searchwrap"><span class="leads-sr">Search leads</span><input class="leads-search" id="leads-search" type="search" placeholder="Search leads" value="${esc(state.search)}"></label>
    <button type="button" class="leads-toolbar-button" id="leads-filters" aria-expanded="${state.filtersOpen}" aria-controls="leads-filter-fields">Filters${secondaryFilterCount ? ` (${secondaryFilterCount})` : ''}</button>
  </div>
  <div class="leads-filter-fields" id="leads-filter-fields" ${state.filtersOpen ? '' : 'hidden'}>
    <label>Lead status<select id="leads-statussel">${options([['ALL','All statuses'], ...STATUSES.map(s => [s,statusLabel(s)])],state.filter)}</select></label>
    <label>Magnet<select id="leads-magnetsel">${options([['ALL','All magnets'],...Object.entries(state.magnetNames)],state.magnetFilter)}</select></label>
    ${!state.workspace ? `<label>Source<select id="leads-sourcesel">${options([['ALL','All sources'], ...state.sourceOptions.map(s => [s === 'Magnet' ? 'MAGNET' : s, s === 'Magnet' ? 'Website inquiries' : s])],state.sourceFilter)}</select></label>` : ''}
    ${state.book ? `<label>Client<select id="leads-clientsel">${options([['ALL','All clients'], ...Object.entries(state.ownerNames)],state.clientFilter)}</select></label>` : ''}
    <label class="leads-view-control">View<select id="leads-view">${options([['compact','Summary list'],['table','Detailed table']], state.view)}</select></label>
    <label>Sort<select id="leads-sort">${options([['captured:desc','Newest capture first'],['captured:asc','Oldest capture first'],['name:asc','Name A–Z'],['name:desc','Name Z–A']],`${state.sort.key}:${state.sort.dir}`)}</select></label>
  </div>
  <div class="leads-active-filters">${filters.filter(([key]) => !state.workspace || key !== 'sourceFilter').map(([key,,label]) => `<button type="button" data-clear="${key}">${esc(label || 'Selected client')} <span aria-hidden="true">×</span><span class="leads-sr">Remove filter</span></button>`).join('')}${dayChipHTML()}
    ${state.from || state.to ? `<button type="button" data-clear="dates">${esc(state.from || 'Start')} – ${esc(state.to || 'Today')} (UTC) ×</button>` : ''}
    ${state.search ? '<button type="button" data-clear="search">Clear search ×</button>' : ''}</div>
  <div class="leads-count" id="leads-countlbl" role="status"></div><div id="leads-tablezone"></div>
  <details class="leads-records-about"><summary>About these records</summary><p class="leads-time-note">Dates use record capture time (UTC). Repeat inquiries can belong to the same record. Status reflects what your team has recorded here.</p></details>`;
  body.querySelector('#leads-filters').addEventListener('click', () => { state.filtersOpen = !state.filtersOpen; renderBody(); body.querySelector('#leads-filters').focus(); });
  const controls = { 'leads-statussel':'filter','leads-magnetsel':'magnetFilter','leads-sourcesel':'sourceFilter','leads-clientsel':'clientFilter','leads-rangesel':'range' };
  for (const [id,key] of Object.entries(controls)) body.querySelector(`#${id}`)?.addEventListener('change', e => {
    state[key] = e.target.value; if (key === 'range') { state.day = ''; state.from = ''; state.to = ''; } refresh();
  });
  body.querySelector('#leads-sort').addEventListener('change', e => { const [key,dir] = e.target.value.split(':'); state.sort = {key,dir}; refresh(); });
  body.querySelector('#leads-view').addEventListener('change', e => { state.view = e.target.value; rememberView(); renderTable(); });
  body.querySelector('#leads-dayclear')?.addEventListener('click', () => { state.day = ''; refresh(); });
  body.querySelectorAll('[data-clear]').forEach(btn => btn.addEventListener('click', () => {
    const key = btn.dataset.clear;
    if (key === 'dates') { state.from = ''; state.to = ''; }
    else if (key === 'identity') { state.leadId = ''; state.leadGuid = ''; }
    else state[key] = ['leadId','leadGuid','search'].includes(key) ? '' : 'ALL'; refresh();
  }));
  body.querySelectorAll('[data-source]').forEach(btn => btn.addEventListener('click', () => { state.sourceFilter = btn.dataset.source === 'Magnet' ? 'MAGNET' : btn.dataset.source; refresh(); }));
  body.querySelector('#leads-search').addEventListener('input', e => { state.search = e.target.value; clearTimeout(searchTimer); if(state.workspace) ++loadEpoch;
    if (state.workspace) searchTimer = setTimeout(refresh, 250); else { state.shown = firstPage(); renderTable(); } });
  renderTable();
  if (focusId) { const el = document.getElementById(focusId); el?.focus(); if (el?.type === 'search' && selection != null) el.setSelectionRange(selection,selection); }
}

function renderTable() {
  const zone = document.getElementById('leads-tablezone'); if (!zone) return;
  const rows = sortedFiltered(), visible = state.workspace ? rows : rows.slice(0,state.shown);
  const total = state.workspace ? state.total : rows.length;
  document.getElementById('leads-countlbl').textContent = total === 1 ? '1 record' : total ? `${state.offset + 1}–${state.offset + visible.length} of ${total.toLocaleString()} records` : 'No matching records';
  const activity = state.offset === 0 && ['ALL', 'Google LSA'].includes(state.sourceFilter) ? state.workspace?.lsa_activity : null;
  const activityHTML = lsaActivityHTML(activity, { book:state.book, ownerNames:state.ownerNames });
  if (activity?.rows?.length) document.getElementById('leads-countlbl').textContent = `${total} individual records · ${activity.calls}${activity.truncated ? '+' : ''} Local Services calls reported`;
  const compact = state.view === 'compact' || !!state.selectedId;
  zone.innerHTML = !visible.length && activityHTML ? '' : !visible.length ? `<div class="leads-empty"><h2>No records in this view</h2><p>Try a different date range or clear your filters.</p><button type="button" id="leads-clear-all">Show all records</button><a href="#/settings/integrations">View sources &amp; connections</a></div>` : compact
    ? `<div id="leads-tbody" class="leads-compact-list">${compactListHTML(visible)}</div>`
    : `<div class="leads-tablewrap"><table class="leads-table${state.book ? ' leads-table--book' : ''}">${colgroup()}<thead><tr>${thSortHTML('name')}${state.book ? '<th>Client</th>' : ''}${thSortHTML('contact')}<th>Interest</th><th>Source</th><th>Status</th>${thSortHTML('captured','num')}<th></th></tr></thead><tbody id="leads-tbody">${visible.map(rowHTML).join('')}</tbody></table></div>`;
  zone.innerHTML = activityHTML + zone.innerHTML;
  zone.innerHTML += state.workspace ? (state.total > 25 ? `<nav class="leads-pagination" aria-label="Lead pages"><button type="button" data-page="previous"${state.offset === 0 ? ' disabled' : ''}>← Previous</button><span>25 records per page</span><button type="button" data-page="next"${state.offset + rows.length >= state.total ? ' disabled' : ''}>Next →</button></nav>` : '') : pageSizeControlHTML({total:rows.length,shown:state.shown,size:state.pageSize,noun:'leads'});
  if (state.truncated) zone.innerHTML += `<p role="status">Only ${state.leads.length} of ${state.total} records are available. Update the service to search the complete history.</p>`;
  zone.querySelector('#leads-clear-all')?.addEventListener('click', () => { Object.assign(state, {filter:'ALL',clientFilter:'ALL',magnetFilter:'ALL',sourceFilter:'ALL',range:'ALL',day:'',from:'',to:'',leadId:'',leadGuid:'',search:''}); refresh(); });
  zone.querySelectorAll('[data-page]').forEach(btn => btn.addEventListener('click', async () => { state.offset += btn.dataset.page === 'next' ? 25 : -25; state.selectedId = null; rememberView(); await load(); document.querySelector('#leads-tablezone .leads-row')?.focus(); }));
  zone.querySelector('thead')?.addEventListener('click', e => { const btn=e.target.closest('[data-sort]'); if (btn && applySort(btn.dataset.sort)) { if (state.workspace) refresh(); else renderTable(); } });
  if (!state.workspace) wirePageSize(zone,{total:rows.length,onSize:(shown,chosen)=>{state.pageSize=chosen;state.shown=shown;renderTable();}});
  const list = zone.querySelector('#leads-tbody');
  list?.addEventListener('change', onStatusChange);
  list?.addEventListener('click', e => { if(e.target.closest('.leads-status,a'))return;const row=e.target.closest('.leads-row');if(row)selectLead(row.dataset.id);});
  list?.addEventListener('keydown',e=>{if(e.target.tagName==='BUTTON')return;if(!['Enter',' '].includes(e.key))return;const row=e.target.closest('.leads-row');if(row&&e.target===row){e.preventDefault();selectLead(row.dataset.id);}});
  renderPanel();
}

async function onStatusChange(e) {
  const sel = e.target.closest('.leads-status');
  if (!sel) return;
  const id = sel.dataset.id;
  const lead = state.leads.find((l) => String(l.id) === String(id));
  if (!lead || !canManageLead(lead)) return;
  const next = sel.value;
  document.querySelectorAll('.leads-status-review').forEach(node => node.remove());
  if (next === lead.status) return;
  if (['QUALIFIED', 'CONVERTED'].includes(next)) {
    sel.value = lead.status || 'NEW';
    const review = document.createElement('section');
    review.className = 'leads-status-review';
    review.setAttribute('aria-label', 'Review status change');
    review.innerHTML = `<h3>Mark as ${esc(statusLabel(next))}?</h3><p>This can send a qualification update to your CRM and Google Ads, depending on your connection settings.</p><div><button type="button" data-save-status>Save ${esc(statusLabel(next))}</button><button type="button" data-cancel-status>Cancel</button></div>`;
    sel.parentElement.append(review);
    review.querySelector('[data-cancel-status]').addEventListener('click', () => { review.remove(); sel.focus(); });
    review.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); review.remove(); sel.focus(); } });
    review.querySelector('[data-save-status]').addEventListener('click', async () => {
      review.querySelectorAll('button').forEach(button => { button.disabled = true; });
      await saveLeadStatus(sel, lead, next);
      if (review.isConnected) { review.remove(); sel.focus(); }
    });
    review.querySelector('[data-save-status]').focus();
    return;
  }
  await saveLeadStatus(sel, lead, next);
}

async function saveLeadStatus(sel, lead, next) {
  if (!sel.isConnected || !canManageLead(lead) || sel.disabled) return;
  const id = lead.id, scope = currentScope;
  const restoreFocus = document.activeElement === sel || !!sel.parentElement.querySelector('.leads-status-review');
  const prev = lead.status;
  sel.disabled = true;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mLead/${encodeURIComponent(id)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: next }),
    });
    if (!(await res.json().catch(() => ({}))).ok) throw new Error();
    if (scope !== currentScope || scopeKey() !== scope || !sel.isConnected) return;
    lead.status = next; sel.value = next; sel.disabled = false; panelLead = null;
    toast(`Marked ${next[0]}${next.slice(1).toLowerCase()}`, 'success');
  } catch {
    if (scope !== currentScope || !sel.isConnected) return;
    sel.value = prev || 'NEW'; sel.disabled = false;
    toast('Could not update status', 'danger'); notice('Could not update status. Please try again.');
    return;
  }
  // Separate from the try above on purpose: a re-render failure here must not
  // also fire the 'danger' toast on top of the 'success' one that already fired
  // for the (successful) status update itself.
  try {
    if (String(id) === state.selectedId) renderPanel(); // keep the inspector badge in sync
    if (state.workspace) await load(); else renderBody();
    if (scope !== currentScope || scopeKey() !== scope || !document.querySelector('.leads-workspace')) return;
    notice(`Status updated to ${statusLabel(next)}.`);
    if (restoreFocus) {
      const target = document.querySelector('#leads-detail-status-select')
        || document.querySelector(`.leads-status[data-id="${CSS.escape(String(id))}"]`)
        || document.querySelector('#leads-notice');
      if (target) { if (target.id === 'leads-notice') target.tabIndex = -1; target.focus({ preventScroll: true }); }
    }
  } catch (e) { console.warn('[leads] re-render after status change failed:', e); }
}

// ── permanent detail panel (inspector) ───────────────────────────────────────
function selectLead(id) {
  const scroller = document.getElementById('app');
  if (!state.selectedId) state.listScroll = Math.round(scroller?.scrollTop || 0);
  state.selectedId = String(id);
  document.querySelectorAll('.leads-row').forEach((tr) =>
    tr.classList.toggle('selected', tr.dataset.id === state.selectedId));
  renderTable();
  if (scroller) scroller.scrollTop = 0;
  document.querySelector('#leads-panel-title')?.focus({ preventScroll: true });
  rememberView();
}

// Latest value of a lead tag (appointment tags are append-only: the last
// appointment_* entries are current, earlier ones are reschedule history).
function lastTag(l, name) {
  const tags = Array.isArray(l.tags) ? l.tags : [];
  for (let i = tags.length - 1; i >= 0; i--) {
    if (tags[i] && tags[i].tag === name && tags[i].value != null) return String(tags[i].value);
  }
  return '';
}

// Calendly appointment off the lead's tags, formatted in the invitee's zone.
function appointmentOf(l) {
  const start = lastTag(l, 'appointment_start');
  if (!start) return null;
  const status = lastTag(l, 'appointment_status') || 'booked';
  const tz = lastTag(l, 'appointment_timezone');
  const type = lastTag(l, 'appointment_event_type');
  const d = new Date(start);
  let when = start;
  if (!Number.isNaN(d.getTime())) {
    const opts = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    try { when = new Intl.DateTimeFormat(undefined, tz ? { ...opts, timeZone: tz } : opts).format(d); }
    catch { when = new Intl.DateTimeFormat(undefined, opts).format(d); }
  }
  return { when, status, type, start };
}

const APPT_BADGE = {
  booked: ['QUALIFIED', 'Booked'],
  rescheduled: ['WORKING', 'Rescheduled'],
  canceled: ['CANCELED', 'Canceled'],
};

// Latest CallRail call off the lead's tags. Calls are appended as GROUPS
// (callrail_call_id first, then that call's call_* tags), so read the last
// group as a unit - independent lastTag() reads would stitch fields from
// different calls together (an older call's recording on a missed call).
function callOf(l) {
  const tags = Array.isArray(l.tags) ? l.tags : [];
  let group = null;
  for (const t of tags) {
    if (!t || !t.tag) continue;
    if (t.tag === 'callrail_call_id') { group = {}; continue; }
    if (group && t.tag.indexOf('call_') === 0 && t.value != null) group[t.tag] = String(t.value);
  }
  if (!group || !group.call_started) return null;
  const started = group.call_started;
  const d = new Date(started);
  let when = started;
  if (!Number.isNaN(d.getTime())) {
    when = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
  }
  const secs = Number(group.call_duration) || 0;
  const dur = secs ? (secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`) : '';
  return {
    when, dur,
    source: group.call_source || '',
    answered: group.call_answered || '',
    recording: group.call_recording_player || '',
    started,
  };
}

// Latest Google Local Services state off the lead's tags. lsa_status and
// lsa_charged are APPENDED on every Google-side change (disputes, bookings),
// so the last value of each is the current truth.
function lsaOf(l) {
  const tags = Array.isArray(l.tags) ? l.tags : [];
  const last = (name) => {
    for (let i = tags.length - 1; i >= 0; i--) {
      if (tags[i] && tags[i].tag === name && tags[i].value != null) return String(tags[i].value);
    }
    return '';
  };
  if (!last('lsa_lead_id')) return null;
  const dispute = last('lsa_dispute_candidate');
  return {
    type: last('lsa_lead_type'),
    category: last('lsa_category'),
    status: last('lsa_status').toLowerCase(),
    charged: last('lsa_charged') === 'yes',
    email: last('lsa_email'),
    missed: last('lsa_missed') === 'yes',
    dispute: dispute && dispute !== 'no' ? dispute : '',
  };
}

/** Ad-call facts off the lead's tags (calls placed from the ad itself). */
function adCallOf(l) {
  const tags = Array.isArray(l.tags) ? l.tags : [];
  const last = (name) => {
    for (let i = tags.length - 1; i >= 0; i--) {
      if (tags[i] && tags[i].tag === name && tags[i].value != null) return String(tags[i].value);
    }
    return '';
  };
  if (!last('ad_call_id')) return null;
  return {
    missed: last('ad_call_status') === 'missed',
    legacyOutcome: last('ad_call_status') === 'answered',
    outcome: last('ad_call_status') === 'missed' ? 'Missed call' : last('ad_call_status') === 'received' ? 'Received call' : last('ad_call_status') === 'answered' ? 'Call recorded' : 'Outcome unavailable',
    dur: last('ad_call_duration'),
    area: last('ad_call_area_code'),
    campaign: last('ad_call_campaign'),
  };
}

function closePanel() {
  const id = state.selectedId; state.selectedId = null;
  renderTable();
  const scroller = document.getElementById('app');
  if (scroller) scroller.scrollTop = state.listScroll || 0;
  [...document.querySelectorAll('.leads-row')].find(row => row.dataset.id === id)?.focus({ preventScroll: true });
  rememberView();
}
function renderPanel() {
  const el = document.getElementById('leads-panel-body'); if (!el) return;
  const l = sortedFiltered().find(x => String(x.id) === state.selectedId);
  if (l && panelLead === l && el.querySelector('#leads-panel-title')) return;
  const workspace = document.querySelector('.leads-workspace');
  const workspaceConnections = workspace?.querySelector('.leads-connections');
  if (workspaceConnections && el.contains(workspaceConnections)) {
    workspaceConnections.open = workspaceConnections.dataset.recordWasOpen === 'true';
    delete workspaceConnections.dataset.recordWasOpen;
    workspace.append(workspaceConnections);
  }
  inquiryHistory?.destroy(); inquiryHistory = null; panelLead = l;
  const layout = document.querySelector('.leads-layout'); if (layout) layout.dataset.hasSelection = l ? 'true' : 'false';
  document.querySelector('.leads-workspace')?.setAttribute('data-has-selection', l ? 'true' : 'false');
  if (!l) { el.innerHTML = ''; return; }
  const ap = appointmentOf(l), cl = callOf(l), ls = lsaOf(l), ac = adCallOf(l);
  const isMagnet = !!l.magnet_guid || l.lead_source === 'Magnet';
  const fields = (entries) => entries.filter(([,v]) => v != null && v !== '').map(([k,v]) => `<div class="leads-det__field"><span class="leads-det__k">${esc(k)}</span><span class="leads-det__v">${esc(v)}</span></div>`).join('');
  const contact = usablePhone(l);
  const rows = sortedFiltered(), index = rows.findIndex(row => String(row.id) === state.selectedId);
  const submissions = (Array.isArray(l.comments) ? l.comments : []).filter(note => note?.source === 'magnet_submission' && typeof note.comment === 'string');
  // Remove only the obsolete sentence from our own generated Ads import note.
  // Stored notes and any customer-authored content remain unchanged.
  const description = ac && l.description?.startsWith('Phone call placed from a Google ad:')
    ? l.description.replace("Google shares the caller's area code only; the full number is in the Google Ads call details report.", '').trim()
    : l.description;
  const actions = `${contact ? `<a class="leads-det__cbtn leads-contact-primary" href="tel:${esc(contact)}">Call ${esc(l.first_name || 'contact')}<small>${esc(l.phone || l.mobile)}</small></a>` : ''}${l.email ? `<a class="leads-det__cbtn${contact ? '' : ' leads-contact-primary'}" href="mailto:${esc(l.email)}">Email ${esc(l.first_name || 'contact')}<small>${esc(l.email)}</small></a>` : ''}`;
  const unavailable = !contact && ac ? 'Google Ads didn’t share a callback number.' : !l.email && !contact ? (cl ? 'No callback number provided.' : 'No contact details provided.') : '';
  const recordTools = [
    ...(description && (isMagnet || ac || cl || ls) ? [{ id:'notes', label:'Notes', icon:'sticky_note_2', title:'Saved notes', content:`<p class="leads-det__notes">${esc(description)}</p>` }] : []),
    { id:'source', label:'Source', icon:'route', title:'Where this came from', content:`${fields([[isMagnet ? 'Magnet' : 'Source',sourceOf(l)],['Campaign',ac?.campaign||l.utm_campaign],['Recorded attribution',[l.utm_source,l.utm_medium].filter(Boolean).join(' / ')]])}${l.magnet_guid?`<a data-magnet-results href="#/magnets/${encodeURIComponent(l.magnet_guid)}/results">View Magnet results →</a>`:''}${l.gclid?'<p>Google Ads click ID recorded.</p>':''}` },
    ...(workspaceConnections ? [{ id:'connections', label:'Connections', icon:'hub', title:'CRM connections', content:'<p>CRMs linked to this workspace. Delivery of this inquiry is not shown here.</p><div data-record-connections></div>' }] : []),
    ...([l.email,l.phone,l.mobile,l.company,l.title,l.product_interest,l.city,l.state].some(Boolean) ? [{ id:'contact', label:'Saved contact', icon:'contact_page', title:'Saved contact', content:fields([['Email',l.email],['Phone',l.phone],['Mobile',l.mobile],['Company',l.company],['Title',l.title],['Interest',l.product_interest],['Location',[l.city,l.state].filter(Boolean).join(', ')]]) }] : []),
    ...(submissions.length ? [{ id:'submissions', label:'Submissions', icon:'history', title:`Additional saved submissions (${submissions.length})`, content:submissions.map(note=>`<p class="leads-det__notes">${esc(note.comment)}</p>`).join('') }] : []),
    ...(canManageLead(l) ? [{ id:'more', label:'More', icon:'more_horiz', title:'More actions', content:`<button type="button" class="leads-det__del" data-action="delete-lead" data-id="${esc(l.id)}">Remove lead</button>` }] : []),
  ];
  const recordToolsHtml = `<div class="leads-record-tools">
    <div class="leads-record-tool-buttons" role="group" aria-label="Record details">
      ${recordTools.map(tool => `<button type="button" class="leads-record-tool" id="lead-tool-${tool.id}" data-record-tool="${tool.id}" aria-expanded="false" aria-controls="lead-tool-panel-${tool.id}"><span class="material-symbols-outlined" aria-hidden="true">${tool.icon}</span><span>${tool.label}</span></button>`).join('')}
    </div>
    ${recordTools.map(tool => `<section class="leads-tool-panel" id="lead-tool-panel-${tool.id}" aria-labelledby="lead-tool-heading-${tool.id}" hidden><header><h2 id="lead-tool-heading-${tool.id}" tabindex="-1">${tool.title}</h2><button type="button" data-close-record-tool="${tool.id}" aria-label="Close ${tool.label}"><span class="material-symbols-outlined" aria-hidden="true">close</span></button></header>${tool.content}</section>`).join('')}
  </div>`;
  const followUpHtml = `<aside class="leads-record-actions" aria-label="Follow-up">
    ${actions ? `<section class="leads-contact-section"><h2>Get in touch</h2><div class="leads-det__contact">${actions}</div></section>` : isMagnet && unavailable ? `<section class="leads-contact-section"><h2>Contact</h2><p class="leads-availability">${esc(unavailable)}</p></section>` : ''}
    <section class="leads-status-section" aria-label="Lead status">
      <div class="leads-detail-status">${canManageLead(l)?'<label for="leads-detail-status-select">Lead status</label>':'<span>Lead status</span>'}${canManageLead(l)?statusSelect(l, { detail: true }):`<span class="leads-badge" data-s="${esc(l.status)}">${esc(statusLabel(l.status))}</span>`}</div>
      ${!canManageLead(l) ? '<p class="leads-muted">View-only access</p>' : ''}
      <details class="leads-status-guide"><summary>About this status</summary><p id="leads-status-help">${esc(STATUS_HELP[l.status] || 'Status recorded in Magnet.')}</p>
      ${canManageLead(l)?'<p id="leads-status-effects" class="leads-status-effects">Marking Qualified or Converted can update your connected tools when enabled.</p>':''}
      <p>Record your team’s progress here. A status does not confirm delivery.</p><dl>${STATUSES.map(s=>`<dt>${esc(statusLabel(s))}</dt><dd>${esc(STATUS_HELP[s])}</dd>`).join('')}</dl></details>
    </section></aside>`;
  el.innerHTML = `<nav class="leads-detail-nav" aria-label="Record navigation"><button type="button" id="leads-close">← Leads</button><strong class="leads-sticky-name">${esc(fullName(l))}</strong><span class="leads-record-position">${index+1} of ${rows.length} on this page</span><button type="button" data-record="previous" aria-label="Previous record"${index<=0?' disabled':''}>←</button><button type="button" data-record="next" aria-label="Next record"${index>=rows.length-1?' disabled':''}>→</button></nav>
    <div class="leads-det__head"><span class="leads-avatar leads-det__ava">${avatarHTML(l)}</span><div><p class="leads-detail-eyebrow">${esc(ac?'Google Ads call':cl?'Tracked call':ls?'Local Services inquiry':isMagnet?'Website inquiry':'Lead record')}</p><h1 class="leads-det__name" id="leads-panel-title" tabindex="-1">${esc(fullName(l))}</h1><p class="leads-muted">Record captured ${esc(when(l))}${state.book ? ` · ${esc(state.ownerNames[l.owner_guid] || 'Client')}`:''}</p></div></div>
    <div class="leads-record-grid">${followUpHtml}<div class="leads-record-main">${recordToolsHtml}
    ${ac||cl ? `<section class="leads-reading leads-call-reading"><p class="leads-eyebrow">Call summary</p><h2>${esc(ac ? ac.outcome : callOutcome(cl.answered))}</h2>${fields(ac?[['Duration',ac.dur],['Caller area code',ac.area]]:[['Started',cl.when],['Duration',cl.dur],['Source',cl.source]])}${ac?.legacyOutcome ? '<p class="leads-availability">Whether this older call was answered is not confirmed.</p>' : ''}${cl?.recording&&/^https:\/\//i.test(cl.recording)?`<a class="leads-det__cbtn" href="${esc(cl.recording)}" target="_blank" rel="noopener">Open recording ↗</a>`:''}${unavailable ? `<p class="leads-availability leads-contact-notice">${esc(unavailable)}</p>` : ''}</section>`:''}
    ${ls?`<section class="leads-reading"><p class="leads-eyebrow">Local Services inquiry</p><h2>${esc(ls.category || ls.type || 'Inquiry details')}</h2>${fields([['Type',ls.type],['Reported status',ls.status],['Charge',ls.charged?'Charged':'Not charged'],['Email',ls.email]])}${ls.missed?'<p>Missed call reported</p>':''}${ls.dispute?'<p>Dispute candidate</p>':''}</section>`:''}
    ${isMagnet?'<section class="lead-inquiry-history" aria-label="Submitted answers"></section>':''}
    ${!isMagnet && !ac && !cl && !ls ? `<section class="leads-reading"><h2>What was recorded</h2>${description ? `<p class="leads-det__notes">${esc(description)}</p>` : '<p>No request details are recorded for this lead.</p>'}${unavailable ? `<p class="leads-availability">${esc(unavailable)}</p>` : ''}</section>` : ''}
    ${ap?`<section class="leads-reading leads-appointment"><h2>Appointment</h2>${fields([['When',ap.when],['Type',ap.type],['Status',ap.status]])}</section>`:''}
    </div></div>`;
  if (workspaceConnections) {
    workspaceConnections.dataset.recordWasOpen = String(workspaceConnections.open);
    workspaceConnections.open = true;
    el.querySelector('[data-record-connections]')?.append(workspaceConnections);
    updateConnectionsTool();
  }
  const showRecordTool = id => {
    for (const tool of recordTools) {
      el.querySelector(`#lead-tool-${tool.id}`).setAttribute('aria-expanded', String(tool.id === id));
      el.querySelector(`#lead-tool-panel-${tool.id}`).hidden = tool.id !== id;
    }
  };
  el.querySelectorAll('[data-record-tool]').forEach(button => button.addEventListener('click', () =>
    {
      const id = button.getAttribute('aria-expanded') === 'true' ? null : button.dataset.recordTool;
      showRecordTool(id);
      if (id) el.querySelector(`#lead-tool-heading-${id}`).focus({ preventScroll: true });
    }));
  el.querySelectorAll('[data-close-record-tool]').forEach(button => button.addEventListener('click', () => {
    showRecordTool(null); el.querySelector(`#lead-tool-${button.dataset.closeRecordTool}`).focus();
  }));
  el.querySelector('.leads-record-tools').addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const expanded = el.querySelector('[data-record-tool][aria-expanded="true"]');
    if (expanded) { event.preventDefault(); showRecordTool(null); expanded.focus(); }
  });
  el.querySelector('#leads-close').addEventListener('click',closePanel);
  el.querySelectorAll('[data-record]').forEach(btn=>btn.addEventListener('click',()=>{const next=rows[index+(btn.dataset.record==='next'?1:-1)];if(next)selectLead(next.id);}));
  el.querySelector('.leads-status')?.addEventListener('change',onStatusChange);
  el.querySelector('[data-magnet-results]')?.addEventListener('click', event => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || scopeKey() !== currentScope) return;
    rememberView();
    const params = queryParams();
    const from = params.get('from'), to = params.get('to');
    setMagnetResultsHandoff({ ...leadsHandoffScope(), ownerGuid: l.owner_guid, magnetGuid: l.magnet_guid,
      ...(from && to ? { from, to } : {}), returnTo: { hash: '#/leads', label: 'Back to leads' } });
  });
  el.querySelector('[data-action="delete-lead"]')?.addEventListener('click',onDeleteLead);
  if(isMagnet){
    inquiryHistory=mountLeadInquiryHistory(el.querySelector('.lead-inquiry-history'),{lead:l,apiBase:apiBase(),presentation:'reading',
      isCurrent:()=>state.selectedId===String(l.id)&&scopeKey()===currentScope&&state.leads.some(item=>item.id===l.id&&item.owner_guid===l.owner_guid)});
    inquiryHistory.open();
  }
}

/**
 * Remove a lead from the dashboard.
 *
 * The endpoint SOFT-deletes: it sets status DELETED and prefixes the email with a
 * timestamp so the same address can be captured again later. The row itself is not
 * destroyed, and this is not a GDPR erasure — so the confirmation says "removed from your
 * dashboard" and deliberately does NOT promise permanent deletion from all systems. A
 * customer acting on a promise we do not keep is worse than a wordier dialog.
 */
async function onDeleteLead(e) {
  const id = e.currentTarget?.dataset?.id;
  const lead = state.leads.find((x) => String(x.id) === String(id));
  if (!lead || !canManageLead(lead)) return;
  const scope = currentScope;

  const who = fullName(lead);
  if (!window.confirm(
    `Remove ${who} from your leads?\n\n`
    + 'This takes the lead out of your dashboard, lists and exports. '
    + 'The record is kept in our system, and this address can be captured again if they '
    + 'come back through one of your magnets.',
  )) return;

  const btn = e.currentTarget;
  btn.disabled = true;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mLead/${encodeURIComponent(id)}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);

    if (scope !== currentScope || scopeKey() !== scope || !btn.isConnected) return;
    // Drop it locally rather than refetching the whole list: the row is already gone
    // server-side, and a failed refresh must not look like a failed delete.
    state.leads = state.leads.filter((x) => String(x.id) !== String(id));
    state.selectedId = null;
    toast(`${who} removed`, 'success');
    if (state.workspace) await load(); else renderBody();
    notice(`${who} removed.`);
  } catch (err) {
    if (scope !== currentScope || !btn.isConnected) return;
    btn.disabled = false;
    toast('Could not remove that lead. Please try again.', 'danger');
  }
}

// ── CSV export (current filter AND current sort) ─────────────────────────────
// Exports what the screen shows, in the order the screen shows it. Handing back a file
// ordered differently from the table the user was just looking at is the kind of small
// mismatch that gets read as the export being wrong.
async function exportCsv() {
  let rows = sortedFiltered();
  const exportFilter = state.filter;
  if (state.workspace) {
    const query = queryParams(1000,0).toString();
    const scope = currentScope; const button = document.getElementById('leads-export');
    if (button) button.disabled = true;
    try {
      notice('Preparing up to 1,000 matching records…');
      const res = await guardedFetch(`${apiBase()}/m5t/v5/m5mLead?${query}`);
      const data = await res.json();
      if (!res.ok || !data.ok || data.workspace?.version !== 1) throw new Error();
      if (scope !== currentScope || scopeKey() !== scope || !button?.isConnected) return;
      if (queryParams(1000,0).toString() !== query) { notice('Filters changed. Export again to use the current view.'); return; }
      rows = data.data;
      notice(data.total > rows.length ? `Export includes the first ${rows.length} of ${data.total} matching records. Narrow your filters for a smaller export.` : `Exported ${rows.length} matching records.`);
    } catch { notice('Could not export leads. Please try again.'); return; }
    finally { if (button?.isConnected) button.disabled = false; }
  }
  if (!rows.length) { toast('Nothing to export', 'info'); return; }
  const cols = ['first_name', 'last_name', 'email', 'phone', 'company', 'product_interest', 'lead_source', 'utm_source', 'utm_medium', 'utm_campaign', 'gclid', 'status'];
  const cell = (v) => { const text = String(v ?? ''); return `"${(/^[=+@\-\t\r]/.test(text) ? "'" + text : text).replace(/"/g, '""')}"`; };
  const header = [...(state.book ? ['client'] : []), ...cols, 'magnet', 'captured', 'appointment', 'last_call'];
  const lines = [header.join(',')];
  for (const l of rows) {
    const ap = appointmentOf(l);
    const cl = callOf(l);
    lines.push([
      ...(state.book ? [cell(state.ownerNames[l.owner_guid] || '')] : []),
      ...cols.map((c) => cell(l[c])), cell(state.magnetNames[l.magnet_guid] || l.lead_source || ''), cell(when(l)),
      cell(ap ? `${ap.start} (${ap.status})` : ''),
      cell(cl ? `${cl.started} (${callOutcome(cl.answered)})` : ''),
    ].join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `leads-${exportFilter.toLowerCase()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast(`Exported ${rows.length} lead${rows.length === 1 ? '' : 's'}`, 'success');
}
